import { MAP_STOP_LABELS } from "../../../../config/global.configuration";
import { type CompactShape } from "../../../../model/realtime/CompactShape";
import {
    formatScheduleArrivalWithDelayToHHMM,
    hasNonEmptyName,
    isValidCoordinate,
} from "../../../../tools/MapItineraryTools";
import { formatStopNameShort } from "../../../../tools/stopNameFormatter";
import { normalizeStopLabel } from "../../../../tools/StringTools";
import type { StopAnchorWire, TripDelayWire } from "../../../../types/dtos/realtime-api/realtime.dto";
import {
    applyActiveStopDotPresentation,
    applyDimmedStopDotPresentation,
    buildStopLabelLine1,
    createStopLabelDot,
    createStopLabelElement,
    resolveStopCorrespondenceRoutes,
    type StopLabelData,
} from "../stopLabels";
import type { TransitPathStopCollisionEntry } from "../stopOverlay";
import {
    isRealtimeRemainingTerminusStop,
    resolveRealtimeTransitPathStopLabelPriority,
} from "../transitPathStopLabelPriority.tools";
import {
    REALTIME_TRANSIT_PATH_DIMMED_MARKER_PRESENTATION,
    REALTIME_TRANSIT_PATH_MARKER_OPACITY_ACTIVE,
    REALTIME_TRANSIT_PATH_MARKER_OPACITY_DIMMED,
    REALTIME_TRANSIT_PATH_MARKER_SATURATE_DIMMED,
} from "./realtimeTransitPath.configuration";

/**
 * Wraps marker content in a root element with uniform opacity.
 *
 * @param content Marker DOM subtree.
 * @param opacity Target opacity in `[0, 1]`.
 * @returns Root element to pass to `maplibregl.Marker`.
 */
export function createOpacityWrappedMarkerElement(
    content: HTMLElement,
    opacity: number,
): HTMLElement {
    if ( opacity >= REALTIME_TRANSIT_PATH_MARKER_OPACITY_ACTIVE ) {
        return content;
    }

    const wrapper = document.createElement( "div" );
    wrapper.className = "pointer-events-none";
    wrapper.style.opacity = String( opacity );
    wrapper.style.filter = `saturate(${ REALTIME_TRANSIT_PATH_MARKER_SATURATE_DIMMED })`;
    wrapper.appendChild( content );

    return wrapper;
}

/**
 * Builds a {@link StopLabelData} for a realtime transit path stop.
 *
 * @param stop GTFS stop anchor from the schedule.
 * @param name Normalised stop name.
 * @param delay Optional trip delay wire data.
 * @returns Presentation-ready stop label data.
 */
export function buildRealtimeTransitStopLabelData(
    stop: StopAnchorWire,
    name: string,
    delay: TripDelayWire | undefined,
): StopLabelData {
    const stopDelay = delay !== undefined
        ? delay.stops.find( ( [ seq ] ) => seq === stop.seq )
        : undefined;
    const delaySec = stopDelay !== undefined ? stopDelay[1] : delay?.global ?? 0;

    const arrivalTime = MAP_STOP_LABELS.realtimeTransitPath.showArrivalTime
        ? formatScheduleArrivalWithDelayToHHMM( stop.arr, delaySec )
        : null;

    const correspondenceRoutes = MAP_STOP_LABELS.realtimeTransitPath.showCorrespondences
        ? resolveStopCorrespondenceRoutes( stop.stop_id )
        : [];

    return {
        name,
        arrivalTime,
        delaySec,
        "hasRealtimeDelay":   delay !== undefined,
        "applyDelayColoring": MAP_STOP_LABELS.realtimeTransitPath.showDelayStatus,
        correspondenceRoutes,
    };
}

/**
 * Builds one realtime transit path stop collision entry.
 *
 * @param stop GTFS stop anchor.
 * @param stopIndex Index within its segment.
 * @param stopCount Stops in the segment.
 * @param allShapePoints Full trip shape.
 * @param colorHex Route color for borders.
 * @param isDarkUi Whether dark mode is active.
 * @param delay Optional trip delay wire.
 * @param markerOpacity Marker opacity for past vs future stops.
 * @param isPast Whether the stop is behind the vehicle.
 * @returns Collision entry, or null when coordinates are invalid.
 * @remarks The first remaining stop uses a filled route-color chip; the final remaining
 *          stop (when distinct) uses an outline terminus chip with the standard dot.
 */
export function buildRealtimeTransitStopCollisionEntry(
    stop: StopAnchorWire,
    stopIndex: number,
    stopCount: number,
    allShapePoints: CompactShape,
    colorHex: string,
    isDarkUi: boolean,
    delay: TripDelayWire | undefined,
    markerOpacity: number,
    isPast: boolean,
): TransitPathStopCollisionEntry | null {
    const shapeIdx = stop.shape_idx;
    if ( shapeIdx < 0 || shapeIdx >= allShapePoints.length ) {
        return null;
    }

    const lat = allShapePoints.latAt( shapeIdx );
    const lon = allShapePoints.lonAt( shapeIdx );
    if ( !isValidCoordinate( lat, lon ) ) {
        return null;
    }

    const isDimmedStop = markerOpacity < REALTIME_TRANSIT_PATH_MARKER_OPACITY_ACTIVE;
    const isNextStop = !isPast && stopIndex === 0;
    const isTerminus = !isPast && isRealtimeRemainingTerminusStop( stopIndex, stopCount );

    const dotEl = createStopLabelDot( colorHex );
    if ( isDimmedStop ) {
        applyDimmedStopDotPresentation(
            dotEl,
            isDarkUi,
            REALTIME_TRANSIT_PATH_DIMMED_MARKER_PRESENTATION,
        );
    } else {
        applyActiveStopDotPresentation( dotEl, isDarkUi );
    }

    const rawName = normalizeStopLabel( stop.name );
    const normalizedName = rawName !== undefined ? formatStopNameShort( rawName ) : undefined;
    const entry: TransitPathStopCollisionEntry = {
        "id":         `realtime-${ isPast ? "past" : "future" }-${ stop.seq }-${ stopIndex }`,
        "lngLat":     [ lon, lat ],
        "priority":   resolveRealtimeTransitPathStopLabelPriority( stopIndex, stopCount, isPast ),
        "dotElement": createOpacityWrappedMarkerElement( dotEl, markerOpacity ),
    };

    if ( !hasNonEmptyName( normalizedName ) ) {
        return entry;
    }

    const stopData = buildRealtimeTransitStopLabelData( stop, normalizedName, delay );

    if ( isNextStop ) {
        const { element, config } = createStopLabelElement(
            {
                "variant":     "endpointChip",
                "legColorHex": colorHex,
                isDarkUi,
                "aboveDot":    true,
            },
            stopData,
            buildStopLabelLine1( stopData ),
        );
        element.className += ` ${ config.className }`;

        entry.labelElement = createOpacityWrappedMarkerElement( element, markerOpacity );
        entry.labelConfig = config;

        return entry;
    }

    if ( isTerminus ) {
        const { element, config } = createStopLabelElement(
            {
                "variant":     "terminusChip",
                "legColorHex": colorHex,
                isDarkUi,
            },
            stopData,
            buildStopLabelLine1( stopData ),
        );
        element.className += ` ${ config.className }`;

        entry.labelElement = createOpacityWrappedMarkerElement( element, markerOpacity );
        entry.labelConfig = config;

        return entry;
    }

    const { element, config } = createStopLabelElement(
        {
            "variant":            isDimmedStop ? "dimmedBubble" : "bubble",
            "legColorHex":        colorHex,
            isDarkUi,
            "dimmedPresentation": isDimmedStop ? REALTIME_TRANSIT_PATH_DIMMED_MARKER_PRESENTATION : undefined,
        },
        stopData,
        buildStopLabelLine1( stopData ),
    );
    element.className += ` ${ config.className }`;

    entry.labelElement = createOpacityWrappedMarkerElement( element, markerOpacity );
    entry.labelConfig = config;

    return entry;
}

/**
 * Builds collision entries for past and remaining realtime transit path stops.
 *
 * @param pastStops Stops already passed by the vehicle.
 * @param remainingStops Stops from the next stop to terminus.
 * @param allShapePoints Full trip shape.
 * @param colorHex Route color for borders.
 * @param isDarkUi Whether dark mode is active.
 * @param delay Optional trip delay wire.
 * @returns Ordered collision entries for both segments.
 */
export function buildRealtimeTransitPathStopCollisionEntries(
    pastStops: readonly StopAnchorWire[],
    remainingStops: readonly StopAnchorWire[],
    allShapePoints: CompactShape,
    colorHex: string,
    isDarkUi: boolean,
    delay: TripDelayWire | undefined,
): TransitPathStopCollisionEntry[] {
    const entries: TransitPathStopCollisionEntry[] = [];

    pastStops.forEach( ( stop, stopIndex ) => {
        const entry = buildRealtimeTransitStopCollisionEntry(
            stop,
            stopIndex,
            pastStops.length,
            allShapePoints,
            colorHex,
            isDarkUi,
            delay,
            REALTIME_TRANSIT_PATH_MARKER_OPACITY_DIMMED,
            true,
        );
        if ( entry !== null ) {
            entries.push( entry );
        }
    } );

    remainingStops.forEach( ( stop, stopIndex ) => {
        const entry = buildRealtimeTransitStopCollisionEntry(
            stop,
            stopIndex,
            remainingStops.length,
            allShapePoints,
            colorHex,
            isDarkUi,
            delay,
            REALTIME_TRANSIT_PATH_MARKER_OPACITY_ACTIVE,
            false,
        );
        if ( entry !== null ) {
            entries.push( entry );
        }
    } );

    return entries;
}
